import { type VariantProps } from 'class-variance-authority';
import {
  CircleSlash,
  HelpCircle,
  MinusCircle,
  Wifi,
  WifiOff,
} from 'lucide-react';
import * as React from 'react';

import { cn } from '../../../lib/utils';

import { connectionStatusBadgeVariants } from './variants';

// Mirrors the DeviceConnectivityStatus vocabulary emitted by the BFF (which in
// turn mirrors the patient service / portal-engine connectivity statuses), plus
// `no_device` — the absence of any connectivity record, which the patient-wide
// lists need and which must never be shown as `disconnected`.
export type ConnectionStatus =
  | 'connected'
  | 'disconnected'
  | 'likely_disconnected'
  | 'no_status'
  | 'unknown'
  | 'unspecified'
  | 'no_device';

export interface ConnectionStatusBadgeProps
  extends
    React.HTMLAttributes<HTMLDivElement>,
    VariantProps<typeof connectionStatusBadgeVariants> {
  status: ConnectionStatus;
  daysInStatus?: number;
  /** @default true */
  showIcon?: boolean;
  iconClassName?: string;
}

const LABELS: Record<ConnectionStatus, string> = {
  connected: 'Connected',
  disconnected: 'Disconnected',
  likely_disconnected: 'Likely disconnected',
  no_status: 'No status',
  unknown: 'Unknown',
  unspecified: 'Unspecified',
  no_device: 'No device',
};

const STATUS_ICONS: Record<ConnectionStatus, typeof Wifi> = {
  connected: Wifi,
  disconnected: WifiOff,
  likely_disconnected: WifiOff,
  no_status: MinusCircle,
  unknown: HelpCircle,
  unspecified: HelpCircle,
  no_device: CircleSlash,
};

export const ConnectionStatusBadge = React.forwardRef<
  HTMLDivElement,
  ConnectionStatusBadgeProps
>(function ConnectionStatusBadge(
  {
    status,
    daysInStatus,
    showIcon = true,
    iconClassName,
    className,
    children,
    size,
    ...props
  },
  ref,
) {
  const displayText = children ?? LABELS[status];
  const Icon = STATUS_ICONS[status];
  const accessibleLabel =
    typeof displayText === 'string' ? displayText : LABELS[status];
  // Connected/disconnected badges omit the status word — the icon carries the
  // meaning (and the word remains in aria-label/title for assistive tech). Only
  // the days-in-status count is shown next to the icon, e.g. "5d".
  const hideLabel =
    children == null && (status === 'connected' || status === 'disconnected');
  // Icon-only badges read as tiny dots in dense tables, so scale the icon (and
  // the count next to it) up beyond the labelled variants' compact sizing.
  const iconSizeClass = hideLabel
    ? size === 'sm'
      ? 'size-4 desktop:size-5'
      : 'size-5'
    : size === 'sm'
      ? 'size-3 desktop:size-3.5'
      : 'size-4';

  return (
    <div
      ref={ref}
      className={cn(
        connectionStatusBadgeVariants({ status, size }),
        // Give the icon-only variant more room and a larger count so it stays
        // legible without the status word to anchor it.
        hideLabel && 'gap-1.5 px-2 py-1 text-sm desktop:px-2.5',
        className,
      )}
      role="status"
      aria-label={accessibleLabel}
      title={accessibleLabel}
      {...props}
    >
      {showIcon && (
        <Icon
          aria-hidden
          className={cn(iconSizeClass, 'shrink-0', iconClassName)}
        />
      )}
      {hideLabel ? (
        daysInStatus ? (
          <span className="min-w-0 truncate font-semibold tabular-nums">{`${daysInStatus}d`}</span>
        ) : null
      ) : (
        displayText != null && (
          <span className="min-w-0 truncate">
            {displayText}
            {daysInStatus ? ` (${daysInStatus}d)` : ''}
          </span>
        )
      )}
    </div>
  );
});
